import type { BoardData, BoardItem } from '@tomo/core';
import { Button, Card, EmptyState, IconButton, Illustration, Menu, useConfirm } from '@tomo/ui';
import { MoreHorizontal, Plus, StickyNote, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useBoardItems, useBoards, usePersonMap, useProject, useReadOnly, useSession } from '../../data/hooks.tsx';
import { relativeTime } from '../../lib/time.ts';
import { ProjectTile } from '../shell/ProjectTile.tsx';
import { inkPath } from './ink.ts';
import './boards.css';

export function BoardsPage() {
  const boards = useBoards();
  const project = useProject();
  const session = useSession();
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const create = () => navigate(`/p/${session.id}/boards/${project.createBoard({ name: 'Untitled board' })}`);
  return (
    <div className="page">
      <header className="page-header">
        <ProjectTile icon="i:shapes" color="yellow" size={40} className="page-header__icon" />
        <div className="page-header__text">
          <h1 className="page-title">Boards</h1>
          <p className="page-desc">Infinite canvases for notes, sketches, shapes and reference art. Cluster ideas, vote on them, then promote the winners.</p>
        </div>
        {!readOnly && (
          <div className="page-header__actions">
            <Button variant="primary" icon={<Plus />} onClick={create}>
              New board
            </Button>
          </div>
        )}
      </header>
      {boards.length === 0 ? (
        <EmptyState
          art={<Illustration name="notes" />}
          title="No boards yet"
          description={readOnly ? 'Boards your team starts show up here.' : 'Start a board for a jam, a retro, a level sketch or a mood wall.'}
          actions={
            !readOnly && (
              <Button variant="primary" icon={<Plus />} onClick={create}>
                New board
              </Button>
            )
          }
        />
      ) : (
        <div className="board-grid">
          {boards.map((b) => (
            <BoardCard key={b.id} board={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function BoardCard({ board }: { board: BoardData }) {
  const items = useBoardItems(board.id);
  const project = useProject();
  const session = useSession();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const people = usePersonMap();
  const readOnly = useReadOnly();
  const author = board.createdBy ? people.get(board.createdBy) : undefined;
  // One vote per person per item, so the score is simply how many people backed each one.
  const votes = items.reduce((s, i) => s + Object.keys(i.votes).length, 0);
  return (
    <Card interactive padding="none" className="board-card" onClick={() => navigate(`/p/${session.id}/boards/${board.id}`)}>
      <BoardThumbnail items={items} />
      <div className="board-card__body">
        <div className="board-card__row">
          <h3 className="board-card__name">{board.name}</h3>
          {!readOnly && (
            <div onClick={(e) => e.stopPropagation()}>
              <Menu
                placement="bottom-end"
                trigger={
                  <IconButton label="Board actions" size="sm">
                    <MoreHorizontal />
                  </IconButton>
                }
                items={[
                  {
                    label: 'Move to trash',
                    icon: <Trash2 />,
                    danger: true,
                    onSelect: async () => {
                      if (await confirm({ title: `Move “${board.name}” to trash?`, confirmLabel: 'Move to trash', danger: true })) project.trashBoard(board.id);
                    },
                  },
                ]}
              />
            </div>
          )}
        </div>
        {board.description && <p className="board-card__desc">{board.description}</p>}
        <div className="board-card__meta">
          <span>
            <StickyNote size={12} /> {items.length} item{items.length === 1 ? '' : 's'}
            {votes > 0 && ` · ${votes} votes`}
          </span>
          <span>{author ? `${author.name} · ` : ''}{relativeTime(board.createdAt)}</span>
        </div>
      </div>
    </Card>
  );
}

/** One item in a board thumbnail: recognisable at this size, no more than that. */
function ThumbItem({ item }: { item: BoardItem }) {
  const { x, y, w, h } = item;
  if (item.type === 'note') return <rect x={x} y={y} width={w} height={h} rx={10} fill={`var(--note-${item.color})`} />;
  // A picture is a solid block; loading real blobs for a preview this small isn't worth it.
  if (item.type === 'image') return <rect x={x} y={y} width={w} height={h} rx={6} fill="var(--fg-subtle)" opacity={0.7} />;
  if (item.type === 'draw')
    return (
      <g data-color={item.color}>
        <path
          d={inkPath(item.points, w, h)}
          transform={`translate(${x} ${y})`}
          fill="none"
          stroke="var(--t-solid)"
          // Thin ink would vanish once the board is scaled down to a card.
          strokeWidth={Math.max(item.strokeWidth, 6)}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  if (item.type === 'shape') {
    const fill = item.filled ? 'var(--t-soft)' : 'none';
    const common = { fill, stroke: 'var(--t-solid)', strokeWidth: Math.max(item.strokeWidth, 6) };
    let body: ReactNode;
    if (item.shape === 'ellipse') body = <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...common} />;
    else if (item.shape === 'diamond') body = <polygon points={`${x + w / 2},${y} ${x + w},${y + h / 2} ${x + w / 2},${y + h} ${x},${y + h / 2}`} {...common} />;
    else if (item.shape === 'rect') body = <rect x={x} y={y} width={w} height={h} rx={8} {...common} />;
    else {
      const [ax = 0, ay = 0, bx = 1, by = 1] = item.points;
      body = <line x1={x + ax * w} y1={y + ay * h} x2={x + bx * w} y2={y + by * h} stroke="var(--t-solid)" strokeWidth={Math.max(item.strokeWidth, 6)} strokeLinecap="round" />;
    }
    return <g data-color={item.color}>{body}</g>;
  }
  // A label: just a bar where its text sits.
  return <rect x={x} y={y + h / 2 - 8} width={Math.min(w, 160)} height={16} rx={8} fill="var(--fg-faint)" opacity={0.6} />;
}

/** Tiny rendering of a board's items, fitted into the card. */
export function BoardThumbnail({ items }: { items: BoardItem[] }) {
  if (!items.length) {
    return (
      <div className="board-thumb board-thumb--empty">
        <StickyNote size={26} />
      </div>
    );
  }
  const minX = Math.min(...items.map((i) => i.x));
  const minY = Math.min(...items.map((i) => i.y));
  const maxX = Math.max(...items.map((i) => i.x + i.w));
  const maxY = Math.max(...items.map((i) => i.y + i.h));
  const pad = 40;
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
  return (
    <div className="board-thumb">
      <svg viewBox={vb} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        {items.map((i) => (
          <ThumbItem key={i.id} item={i} />
        ))}
      </svg>
    </div>
  );
}
